import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import MessageThread from "@/components/MessageThread";
import MessageInput from "@/components/MessageInput";
import { normalizeClientType } from "@/lib/clientTypes";
import { formatParisDate, formatParisTime } from "@/lib/parisDateTime";

type CaseDetail = {
  id: string;
  title: string;
  company_name: string | null;
  client_type: string | null;
  center: string | null;
  event_date: string | null;
  budget: number | null;
  status: string;
};

type Thread = { id: string };

export default function CaseDetailPage() {
  const { id } = useParams();
  const [c, setC] = useState<CaseDetail | null>(null);
  const [thread, setThread] = useState<Thread | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data: cases } = await supabase.from("cases").select("*").eq("id", id).limit(1);
      setC(
        cases?.[0]
          ? {
              ...cases[0],
              client_type: normalizeClientType(cases[0].client_type),
            }
          : null
      );
      const { data: threads } = await supabase.from("threads").select("id").eq("case_id", id).order("created_at", { ascending: true }).limit(1);
      setThread(threads?.[0] ?? null);
    })();
  }, [id]);

  const formatEventDateTime = (eventDate: string | null): string => {
    if (!eventDate) return "";
    try {
      return `${formatParisDate(eventDate, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })} ${formatParisTime(eventDate, {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } catch {
      return eventDate;
    }
  };

  if (!id) return <div className="container py-8">Invalid id</div>;
  if (!c) return <div className="container py-8">Loading...</div>;

  return (
    <div className="container py-8 space-y-6">
      <div className="p-4 border rounded-md">
        <div className="text-lg font-semibold">{c.title}</div>
        <div className="text-sm text-muted-foreground">{c.company_name ?? ""} — {c.center ?? ""}</div>
        <div className="text-sm">Event: {formatEventDateTime(c.event_date)} — Budget: {c.budget ?? ""} — Status: {c.status}</div>
      </div>
      {thread && (
        <div className="space-y-4">
          <MessageThread threadId={thread.id} />
          <MessageInput threadId={thread.id} />
        </div>
      )}
    </div>
  );
}
